import { Calendar, Plane, Plus, User } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SWRApiError } from "@/hooks/swr";
import { TestLabel } from "@/types/testLabel";
import { InfoAlert } from "../../shared/Alerts";
import { Switch } from "../../ui/switch";
import { useState } from "react";
import { Label } from "../../ui/label";
import { StepCardWrapper } from "./StepCardWrapper";

type TestLabelsStepProps = {
  error?: SWRApiError;
  isLoading: boolean;
  isLocked: boolean;
  openTestFlightDialog: (state: boolean) => void;
  openTestLabelDialog: (state: boolean) => void;
  setTestLabelIdForTestFlight: (id: number) => void;
  testLabels: TestLabel[];
};

const TestLabelsStep = ({
  error,
  isLoading,
  isLocked,
  openTestFlightDialog,
  openTestLabelDialog,
  setTestLabelIdForTestFlight,
  testLabels,
}: TestLabelsStepProps) => {
  const [showArchivedTestLabels, setShowArchivedTestLabels] = useState(false);

  if (isLoading) {
    return (
      <StepCardWrapper title="Test Labels">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          Loading test labels...
        </div>
      </StepCardWrapper>
    );
  }

  if (error) {
    return (
      <StepCardWrapper title="Test Labels">
        <div className="text-sm text-red-600">Error loading test labels</div>
      </StepCardWrapper>
    );
  }

  if (testLabels.length === 0) {
    return (
      <StepCardWrapper title="Test Labels">
        <InfoAlert>No Test Label linked.</InfoAlert>
        <Button disabled={isLocked} className="w-full" onClick={() => openTestLabelDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Test Label
        </Button>
      </StepCardWrapper>
    );
  }

  const applyTestLabelStatusStyles = (testLabel: TestLabel) => {
    if (testLabel.isArchived) {
      return "bg-yellow-50 border-yellow-200";
    }

    if (testLabel.status === "VALIDATED") {
      return "bg-green-50 border-green-200";
    }

    return "bg-gray-50 border-gray-200";
  };

  return (
    <StepCardWrapper title="Test Labels">
      <div className="grid gap-4">
        <div className="flex justify-end items-center gap-2">
          <Label htmlFor="show-archived" className="text-xs font-medium">
            Show Archived
          </Label>
          <Switch
            id="show-archived"
            checked={showArchivedTestLabels}
            onCheckedChange={setShowArchivedTestLabels}
          />
        </div>
        {testLabels.length > 0 ? (
          <div className="grid gap-2">
            {testLabels
              .filter((testLabel) => (showArchivedTestLabels ? true : !testLabel.isArchived))
              .map((testLabel) => (
                <div
                  key={testLabel.id}
                  className={`p-3 rounded-md border ${applyTestLabelStatusStyles(testLabel)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Link
                        to={`/test-labels/${testLabel.id}`}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        {testLabel.name}
                      </Link>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {testLabel.createdAt.toLocaleDateString("en-US", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                        {testLabel.createdBy && (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{testLabel.createdBy}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant={testLabel.status === "VALIDATED" ? "default" : "secondary"}
                      className={
                        testLabel.status === "VALIDATED"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-gray-200 text-gray-800 border-gray-200"
                      }
                    >
                      {testLabel.isArchived
                        ? "Archived"
                        : testLabel.status === "VALIDATED"
                          ? "Validated"
                          : "Not Validated"}
                    </Badge>
                  </div>
                  {testLabel.description && (
                    <p className="text-xs text-gray-600 mb-3">{testLabel.description}</p>
                  )}
                  {testLabel.status !== "VALIDATED" && !testLabel.isArchived && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={() => {
                          setTestLabelIdForTestFlight(testLabel.id);
                          openTestFlightDialog(true);
                        }}
                      >
                        <Plane className="w-3 h-3 mr-1" />
                        Create Test Flight
                      </Button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : null}
        <Button className="w-full" onClick={() => openTestLabelDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Test Label
        </Button>
      </div>
    </StepCardWrapper>
  );
};

export default TestLabelsStep;
